import React, { useContext, useState, useEffect, memo } from "react";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import { Box } from "@mui/material";
import { useIntl } from "react-intl";
import { useTheme } from "@mui/styles";
import { get, isEmpty, isObject, isArray } from "lodash-es";
import { IconTooltip } from "@metacrm/metacrm-material-ui/dist/IconTooltip";
import NoDataComponent from "features/metadesk/CommunityAnalysis/components/chart/NoDataComponent";
import EmptyIconStatus from "components/EmptyState/EmptyIconStatus";

function mergeArrayToObject(arr) {
  return arr.reduce((acc, curr) => {
    acc.name = curr.name; // 名稱相同，直接設置
    acc.total = (acc.total || 0) + curr.total; // 累加 total
    return acc;
  }, {});
}

const BarChart = ({ chartData }) => {
  const { formatMessage } = useIntl();
  // 計算每個 series 的總和
  const totals = chartData?.seriesData.map((s) => {
    const newSData = isObject(s.data[0])
      ? s.data.map((item) => item.value)
      : s.data;
    return {
      name: s.name,
      total: newSData.reduce((acc, val) => {
        const newVal = val === "N/A" ? 0 : val;
        return acc + newVal;
      }, 0),
    };
  });
  const sumTotal = totals.reduce((acc, item) => acc + item.total, 0);

  const getOptions = () => {
    return {
      // 启用数据压缩
      progressive: 400, // 每次渲染400个数据点
      progressiveThreshold: 3000, // 超过3000个数据点时启用分段渲染
      animation: false,
      legend: {
        show: true, // 確保圖例顯示
        orient: "horizontal", // 將圖例設置為水平排列
        left: "left", // 水平居中
        itemWidth: 12, // 減小圖例標記的寬度
        itemHeight: 8, // 減小圖例標記的高度
        formatter: function (name) {
          const series = totals.filter((s) => s.name === name);
          const mergeSeries = mergeArrayToObject(series);
          if (!isEmpty(series)) {
            return `{name|${mergeSeries.name}}\n{total|${mergeSeries.total}}`;
          }

          return name;
        },
        textStyle: {
          lineHeight: 18,
          rich: {
            name: {
              fontSize: 12,
              color: "#333",
              verticalAlign: "top", // 讓文字與方塊對齊
            },
            total: {
              fontSize: 18,
              fontWeight: 700,
              color: "#333",
            },
          },
        },
        top: "0",
      },
      grid: {
        top: "90px", // 控制圖表上方距離
        bottom: "0%", // 控制圖表下方距離
        left: "0%", // 控制圖表左側距離
        right: "0%", // 控制圖表右側距離
        containLabel: true, // 確保標籤不會被裁切
      },
      tooltip: {
        trigger: "axis",
        formatter: chartData.formatter
          ? chartData.formatter
          : function (params) {
              const date = moment(params[0].name).format("DD MMM YYYY"); // 取得X軸的日期

              let result = `<div style="margin-bottom:15px">${date}</div>`;
              params.forEach((param) => {
                result += `<div style="min-width:150px; display: flex; align-items: center;">${param.marker} ${param.seriesName}: <span style="margin-left:auto">${param.data}</span></div>`;
              });
              return result;
            },
      },

      xAxis: {
        type: "category",
        data: chartData?.showDate, // 保留完整日期資料
        axisLabel: {
          formatter: function (value) {
            return moment(value).format("DD MMM"); // 隱藏年份
          },
        },
      },
      yAxis: {
        type: "value",
      },
      // tooltip: {
      //   trigger: "item", // 在 bar 上顯示 tooltip
      //   formatter: (params) => {
      //     console.log("params: ", params);

      //     // return content;
      //   },
      // },
      series: chartData?.seriesData,
    };
  };

  return isEmpty(chartData.seriesData) || sumTotal === 0 ? (
    <EmptyIconStatus
      icon="chart"
      emptyTitle={formatMessage({ id: "emptyState.barChart.title" })}
      emptyDescription={formatMessage({
        id: "emptyState.barChart.description",
      })}
    />
  ) : (
    <ReactECharts
      option={getOptions()}
      autoResize={true}
      style={{ height: "100%" }}
      notMerge={true}
    />
  );
};

export default memo(BarChart);
